import React, { useState, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import Swal from "sweetalert2";
import PropTypes from "prop-types";
import {
  API_BASE_URL,
  GET_EXPENSE_TYPE,
  GET_SUPPLIERS,
  GET_USER_STORES,
} from "../../Config";

const AddExpenses = ({ isOpen, onClose, onExpenseAdded }) => {
  const [userDataSession, setUserDataSession] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [stores, setStores] = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedExpenseType, setSelectedExpenseType] = useState(null);
  const [billDate, setBillDate] = useState("");
  const [billAmount, setBillAmount] = useState("");
  const [paidAmount, setPaidAmount] = useState("");
  const [paidDate, setPaidDate] = useState("");
  const [referenceNo, setReferenceNo] = useState("");
  const [notes, setNotes] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const userDataString = sessionStorage.getItem("userData");
    if (userDataString) {
      setUserDataSession(JSON.parse(userDataString));
    }
  }, []);

  useEffect(() => {
    if (!userDataSession?.OrgId || !userDataSession?.UserId || !isOpen) return;
    const fetchData = async () => {
      try {
        const [supRes, storeRes, typeRes] = await Promise.all([
          axios.get(`${API_BASE_URL}${GET_SUPPLIERS}/${userDataSession.OrgId}`),
          axios.get(
            `${API_BASE_URL}${GET_USER_STORES}?UserId=${userDataSession.UserId}`,
          ),
          axios.get(
            `${API_BASE_URL}${GET_EXPENSE_TYPE}?OrgId=${userDataSession.OrgId}`,
          ),
        ]);

        setSuppliers(
          supRes.data?.ResultData?.map((s) => ({
            value: s.Id,
            label: s.SupplierName || s.ContactName || "Unnamed",
          })) || [],
        );
        setStores(
          storeRes.data?.ResultData?.map((s) => ({
            value: s.StoreId || s.Id,
            label: s.Name,
          })) || [],
        );
        setExpenseTypes(
          typeRes.data?.ResultData?.map((t) => ({
            value: t.Id,
            label: t.Name,
            isFixed: t.IsFixed,
          })) || [],
        );
      } catch (err) {
        console.error("Dropdown fetch failed:", err);
      }
    };
    fetchData();
  }, [userDataSession, isOpen]);

  const dueAmount =
    (parseFloat(billAmount) || 0) - (parseFloat(paidAmount) || 0);
  const isDueNegative = dueAmount < 0;

  const resetForm = () => {
    setSelectedSupplier(null);
    setSelectedStore(null);
    setSelectedExpenseType(null);
    setBillDate("");
    setBillAmount("");
    setPaidAmount("");
    setPaidDate("");
    setReferenceNo("");
    setNotes("");
    setImageFile(null);
    setErrorMsg("");
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
    } else {
      setImageFile(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    if (!selectedStore?.value) return setErrorMsg("Store is required");
    if (!selectedSupplier?.value) return setErrorMsg("Supplier is required");
    if (!selectedExpenseType?.value)
      return setErrorMsg("Expense Type is required");
    if (!billDate) return setErrorMsg("Bill Date is required");
    if (!billAmount || parseFloat(billAmount) <= 0)
      return setErrorMsg("Bill Amount must be > 0");
    if (isDueNegative)
      return setErrorMsg("Paid Amount cannot exceed Bill Amount");

    setLoading(true);

    const formData = new FormData();
    formData.append("OrgId", userDataSession?.OrgId);
    formData.append("StoreId", selectedStore.value);
    formData.append("SupplierId", selectedSupplier.value);
    formData.append("ExpenseTypeId", selectedExpenseType.value);
    formData.append("DateOfTransaction", billDate);
    formData.append("Amount", billAmount);
    formData.append("Quantity", 0);
    formData.append("ReferenceNo", referenceNo.trim());
    formData.append("PaidAmount", paidAmount || 0);
    formData.append("PaidDate", paidDate || "");
    formData.append("DueAmount", dueAmount.toFixed(2));
    formData.append("IsApproved", 1);
    formData.append("IsFixed", selectedExpenseType.isFixed ? 1 : 0);
    formData.append("CreatedBy", userDataSession?.UserId);
    formData.append("Note", notes.trim());
    formData.append("Status", dueAmount.toFixed(2) > 0 ? 0 :  1);
    if (imageFile) formData.append("image", imageFile);
    try {
      const res = await axios.post(
        `${API_BASE_URL}Misc/InsertExpense`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      if (res.data?.message?.toLowerCase().includes("success")) {
        Swal.fire({
          title: "Success!",
          text: "Expense added successfully.",
          icon: "success",
          timer: 1800,
          showConfirmButton: true,
        }).then(() => {
          resetForm();
          if (onExpenseAdded) onExpenseAdded();
          onClose();
        });
      } else {
        setErrorMsg(res.data?.error || "Failed to save expense");
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.error || "Server error occurred");
      console.error("Submit error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Reset form when offcanvas is closed (same as ApplyLeave)
  useEffect(() => {
    const offcanvasEl = document.getElementById("offcanvasAddExpense");
    const handleOffcanvasClose = () => {
      resetForm();
    };

    if (offcanvasEl) {
      offcanvasEl.addEventListener("hidden.bs.offcanvas", handleOffcanvasClose);
    }

    return () => {
      if (offcanvasEl) {
        offcanvasEl.removeEventListener(
          "hidden.bs.offcanvas",
          handleOffcanvasClose,
        );
      }
    };
  }, []);

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        .offcanvas {
          transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
          visibility: hidden;
          will-change: transform;
        }
        .offcanvas.show {
          visibility: visible;
        }
        .offcanvas-backdrop {
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        .offcanvas-backdrop.show {
          opacity: 0.5;
        }
        @media (max-width: 576px) {
          #offcanvasAddExpense { width: 90% !important; }
        }
        @media (min-width: 577px) and (max-width: 992px) {
          #offcanvasAddExpense { width: 55% !important; }
        }
        @media (min-width: 993px) and (max-width: 1440px) {
          #offcanvasAddExpense { width: 45% !important; }
        }
        @media (min-width: 1441px) and (max-width: 2550px) {
          #offcanvasAddExpense { width: 30% !important; }
        }
        @media (min-width: 2551px) {
          #offcanvasAddExpense { width: 25% !important; }
        }
      `}</style>

      {/* Backdrop with blur effect */}
      <div
        className={`offcanvas-backdrop fade ${isOpen ? "show" : ""}`}
        style={{
          pointerEvents: isOpen ? "auto" : "none",
          zIndex: 1040,
        }}
        onClick={onClose}
      />

      {/* Loading overlay */}
      {loading && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            zIndex: 1051,
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(255, 255, 255, 0.2)",
          }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      <div
        className={`offcanvas offcanvas-end ${isOpen ? "show" : ""}`}
        tabIndex="-1"
        id="offcanvasAddExpense"
        aria-labelledby="offcanvasAddExpenseLabel"
        style={{ pointerEvents: isOpen ? "auto" : "none" }}
      >
        <div className="offcanvas-header">
          <h5 className="offcanvas-title" id="offcanvasAddExpenseLabel">
            Add New Expense
          </h5>
          <button
            type="button"
            className="btn-close text-reset"
            onClick={onClose}
            aria-label="Close"
          />
        </div>
        <hr className="m-0 p-0" />

        <div className="offcanvas-body">
          {errorMsg && (
            <div
              className="alert alert-danger alert-dismissible fade show"
              role="alert"
            >
              {errorMsg}
              <button
                type="button"
                className="btn-close"
                onClick={() => setErrorMsg("")}
                aria-label="Close"
              />
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              {/* Store Dropdown */}
              <div className="col-md-4">
                <label className="form-label">Store <span className="text-danger">*</span></label>
                <Select
                  placeholder="Select Store"
                  value={selectedStore}
                  onChange={setSelectedStore}
                  options={stores}
                  isClearable
                  isSearchable
                />
              </div>

              {/* Supplier Dropdown */}
              <div className="col-md-4">
                <label className="form-label">Supplier <span className="text-danger">*</span></label>
                <Select
                  placeholder="Select Supplier"
                  value={selectedSupplier}
                  onChange={setSelectedSupplier}
                  options={suppliers}
                  isClearable
                  isSearchable
                />
              </div>

              {/* Expense Type */}
              <div className="col-md-4">
                <label className="form-label">Expense Type <span className="text-danger">*</span></label>
                <Select
                  placeholder="ExpenseType"
                  value={selectedExpenseType}
                  onChange={setSelectedExpenseType}
                  options={expenseTypes}
                  isClearable
                  isSearchable
                />
              </div>

              {/* Reference No */}
              <div className="col-md-4">
                <label className="form-label">Reference No <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Invoice No / PO No"
                  value={referenceNo}
                  onChange={(e) => setReferenceNo(e.target.value)}
                  maxLength={50}
                />
              </div>

              {/* Bill Date */}
              <div className="col-md-4">
                <label className="form-label">Bill Date <span className="text-danger">*</span></label>
                <input
                  type="date"
                  className="form-control"
                  value={billDate}
                  onChange={(e) => setBillDate(e.target.value)}
                  required
                />
              </div>

              {/* Bill Amount */}
              <div className="col-md-4">
                <label className="form-label">Bill Amount <span className="text-danger">*</span></label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="0.00"
                  value={billAmount}
                  onChange={(e) => setBillAmount(e.target.value)}
                  min="0"
                  step="0.01"
                  onWheel={(e) => e.target.blur()}
                  required
                />
              </div>

              {/* Paid Date */}
              <div className="col-md-4">
                <label className="form-label">Paid Date </label>
                <input
                  type="date"
                  className="form-control"
                  value={paidDate}
                  onChange={(e) => setPaidDate(e.target.value)}
                />
              </div>

              {/* Paid Amount */}
              <div className="col-md-4">
                <label className="form-label">Paid Amount </label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="0.00"
                  value={paidAmount}
                  onChange={(e) => setPaidAmount(e.target.value)}
                  min="0"
                  step="0.01"
                  onWheel={(e) => e.target.blur()}
                />
              </div>

              {/* Due Amount */}
              <div className="col-md-4">
                <label className="form-label">Due Amount</label>
                <input
                  type="text"
                  className="form-control bg-light"
                  value={dueAmount.toFixed(2)}
                  readOnly
                />
                {isDueNegative && (
                  <small className="text-danger d-block mt-1">
                    Paid amount cannot exceed bill amount
                  </small>
                )}
              </div>

              {/* Image Upload */}
              <div className="col-12">
                <label className="form-label">
                  Upload Bill / Receipt (optional)
                </label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*,.pdf"
                  onChange={handleImageChange}
                />
              </div>

              {/* Notes */}
              <div className="col-12">
                <label className="form-label">Reason / Notes</label>
                <textarea
                  className="form-control"
                  rows={4}
                  placeholder="Purpose of expense, additional remarks..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  maxLength={600}
                />
                <small className="text-muted d-block mt-1">
                  {notes.length} / 600 characters
                </small>
              </div>

              {/* Buttons */}
              <div className="d-flex justify-content-end mt-4">
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={onClose}
                  disabled={loading}
                >
                  Close
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading || isDueNegative}
                >
                  {loading ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

AddExpenses.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onExpenseAdded: PropTypes.func,
};

AddExpenses.defaultProps = {
  onExpenseAdded: () => {},
};

export default AddExpenses;
